/**
 * @fileoverview Guarded dialog close hook.
 * @description A dialog's close handler that does nothing while its command is in flight. Closing a
 * dialog (Cancel, Escape, the backdrop) while a request is pending would unmount it before the
 * outcome is known, and the admin would never see whether the change was made; holding the close
 * until the request settles keeps the confirmation, any error and a retry in front of them. Shared
 * by the Users and Sales dialogs.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useCallback } from 'react'

/**
 * @description Wraps a dialog's close handler so it is ignored while a command is pending.
 * @param {() => void} onClose - Dismisses the dialog.
 * @param {boolean} pending - Whether the dialog's command is in flight.
 * @returns {() => void} The handler for Cancel, Escape and the backdrop.
 */
export function useGuardedClose(onClose: () => void, pending: boolean): () => void {
  return useCallback(() => {
    if (!pending) {
      onClose()
    }
  }, [onClose, pending])
}
